// @file Make sections sortable by drag and drop on surface.
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import type { SectionDragEvent } from '@@/bits/surface_sortable'
import {
  createAndInsertCloneAtOriginalPosition,
  getIndicatorColor,
  getSortableOptions,
  NO_DRAG_SELECTOR,
  setDirectionForDraggingElement,
  toSectionDragEvent,
  WALL_CONTAINER_ID,
  WISH_LIST_ID,
} from '@@/bits/surface_sortable'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { BreakPoints } from '@@/pinia/window_size'
import { useForceChangeCursor } from '@@/vuecomposables/force_change_cursor'
import { storeToRefs } from 'pinia'
import type Sortable from 'sortablejs'
import { computed, nextTick, onMounted, onUnmounted, watch } from 'vue'

const GROUP_HEADER_SELECTOR = '.surface-group-title'
const SECTION_SELECTOR = '.surface-section'

export function useSurfaceSectionsDragSortable({
  onDragStart,
  onDragEnd,
}: {
  onDragStart?: (e: SectionDragEvent) => void
  onDragEnd?: (e: SectionDragEvent) => void
}): void {
  const { forceChangeCursor, resetCursor } = useForceChangeCursor()

  const { backgroundLuminance, dir, format, isOverlayVisible } = storeToRefs(useSurfaceStore())
  const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
  const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())

  const indicatorColor = computed(() => getIndicatorColor(backgroundLuminance.value === 'dark'))
  const isRtl = computed(() => dir.value === 'rtl')

  /**
   * SORTABLE INSTANCE
   */
  let sortable: Sortable | null = null

  /**
   * STYLES WHEN DRAGGING
   */

  let sortableDefaultsStyleTag: HTMLStyleElementWrapper | null = null
  let hidePostsStyleTag: HTMLStyleElementWrapper | null = null
  let hideSectionActionButtonsStyleTag: HTMLStyleElementWrapper | null = null
  let indicatorLinePositionStyleTag: HTMLStyleElementWrapper | null = null
  let indicatorLineTransformationStyleTag: HTMLStyleElementWrapper | null = null
  let sectionHeightStyleTag: HTMLStyleElementWrapper | null = null
  let dragSizeStyleTag: HTMLStyleElementWrapper | null = null
  let unwrapTimelineLayoutStyleTag: HTMLStyleElementWrapper | null = null

  /**
   * (onStart)
   * Stop timeline layout from centering posts, this helps with reposition scroll position before drag.
   */
  function unwrapTimelineLayout(): void {
    unwrapTimelineLayoutStyleTag = appendCssToDocumentHead(`
      #${WALL_CONTAINER_ID} > .justify-center {
        justify-content: start !important;
      }
    `)
  }

  function hidePosts(): void {
    hidePostsStyleTag = appendCssToDocumentHead(`
    .wish-list-scrolling-wrapper, .group-posts {
      visibility: hidden;
      height: 0;
      margin: 0;
    }`)
  }

  function hideSectionActionButtons(): void {
    hideSectionActionButtonsStyleTag = appendCssToDocumentHead(`
      .surface-section-action-buttons {
        visibility: hidden;
        height: 0;
        margin: 0;
      }
    `)
  }
  /**
   * (onStart) Add styles for cursor, cloned post, posts and indicator line on drag
   */
  function setDraggingStyles(): void {
    hidePosts()
    hideSectionActionButtons()
    const xOffset = 0
    const indicatorMarginStyle =
      format.value === 'timeline' || format.value === 'timeline_v2'
        ? `
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child {
        margin: 8px 0 0px !important;
      }
      @media (max-width: ${BreakPoints.TabletPortrait}px) {
        ${SECTION_SELECTOR}.sortable-indicator-line:first-child {
          margin-top: -8px !important;
        }
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child + ${SECTION_SELECTOR} {
        margin-top: 16px !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:not(:first-child){
        margin: -4px 0 4px !important;
      }`
        : `
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child {
        margin-top: -8px !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child + ${SECTION_SELECTOR} {
        margin-top: 8px !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:not(:first-child){
        margin: 16px 0 -16px !important;
      }
    `
    indicatorLineTransformationStyleTag = appendCssToDocumentHead(`
      ${indicatorMarginStyle}
      ${SECTION_SELECTOR}.sortable-indicator-line {
        visibility: hidden;
        height: 0;
        position: relative;
        overflow: visible !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header {
        visibility: hidden;
        height: 2px;
        position: relative;
        overflow: visible !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header::before {
        content: '';
        visibility: visible;
        border-bottom: 2px solid ${indicatorColor.value};
        height: 0;
        width: ${persistentCloneHeaderElement?.getBoundingClientRect().width ?? 0}px;
        position: absolute;
        top: 0;
        left: auto;
        right: auto;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header > div {
        display: none;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header > div:first-child {
        display: flex;
        height: 0;
        width: ${persistentCloneHeaderElement?.getBoundingClientRect().width ?? 0}px;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header > div:first-child::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        left: ${-xOffset}px;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > header > div:first-child::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        right: ${-xOffset}px;
      }
    `)
  }

  /**
   * (onStart) Add styles for dragging on grid
   */
  function setGridDraggingStyles(): void {
    sectionHeightStyleTag = appendCssToDocumentHead(
      `
      ${SECTION_SELECTOR} {
        height: 100% !important;
      }
      `,
    )
    const xOffset = 0

    indicatorLineTransformationStyleTag = appendCssToDocumentHead(`
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child {
        margin-inline: auto 0px !important;
        transform: translateX(-8px);
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:first-child + ${SECTION_SELECTOR} {
        margin-left: 0 !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line:not(:first-child) {
        margin-inline: 0px !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line {
        visibility: hidden;
        width: 0;
        position: relative;
        overflow: visible !important;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line::before {
        content: '';
        visibility: visible;
        border-left: 2px solid ${indicatorColor.value};
        width: 0;
        height: 100%;
        position: absolute;
        top: 0;
        left: auto;
        right: auto;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > div:first-child, ${SECTION_SELECTOR}.sortable-indicator-line > div:first-child > header {
        width: 250px;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > div:last-child {
        display: flex;
        width: 0;
        height: 100%;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > div:last-child::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        left: -2px;
        top: ${-xOffset}px;
      }
      ${SECTION_SELECTOR}.sortable-indicator-line > div:last-child::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        left: -2px;
        bottom: ${-xOffset}px;
      }
    `)
  }

  /**
   * (onEnd) Remove styles for cursor, cloned post, posts and indicator line after drag
   */
  function removeDraggingStyles(): void {
    sectionHeightStyleTag?.remove()
    sectionHeightStyleTag = null
    dragSizeStyleTag?.remove()
    dragSizeStyleTag = null
    hidePostsStyleTag?.remove()
    hidePostsStyleTag = null
    hideSectionActionButtonsStyleTag?.remove()
    hideSectionActionButtonsStyleTag = null
    indicatorLinePositionStyleTag?.remove()
    indicatorLinePositionStyleTag = null
    indicatorLineTransformationStyleTag?.remove()
    indicatorLineTransformationStyleTag = null
    unwrapTimelineLayoutStyleTag?.remove()
    unwrapTimelineLayoutStyleTag = null
  }

  /**
   * STYLES WHEN DRAGGING
   **/

  let persistentCloneWrapperElement: HTMLElement | null = null
  let persistentCloneHeaderElement: HTMLElement | null = null
  /**
   * (onStart) Create perisistent clone.
   * @param e SortableEvent
   */
  function createPersistentClone(e: Sortable.SortableEvent): void {
    const { cloneWrapperElement, cloneTargetElement } = createAndInsertCloneAtOriginalPosition(e, GROUP_HEADER_SELECTOR)
    persistentCloneWrapperElement = cloneWrapperElement
    persistentCloneHeaderElement = cloneTargetElement
  }

  /**
   * (onEnd) Remove persistent clone
   */
  function removePersistentCloneWrapperElement(): void {
    if (persistentCloneWrapperElement == null) return
    persistentCloneWrapperElement.remove()
    persistentCloneWrapperElement = null
  }

  let screenTopOnStart = 0
  let screenTopOnEnd = 0

  let listPaddingStyleTag: HTMLStyleElementWrapper | null = null
  function addTemporaryListTopPadding(padding: number): void {
    if (padding < 0) return
    listPaddingStyleTag = appendCssToDocumentHead(`
      #${WISH_LIST_ID} {
        padding-top: ${padding}px;
      }
    `)
  }

  function addTemporaryListBottomPadding(padding: number): void {
    if (padding < 0) return
    listPaddingStyleTag = appendCssToDocumentHead(`
      #${WISH_LIST_ID} {
        padding-bottom: ${padding}px;
      }
    `)
  }
  function removeTemporaryListPadding(): void {
    listPaddingStyleTag?.remove()
    listPaddingStyleTag = null
  }

  function rememberScreenTopPositionOnStart(e: Sortable.SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const screenTop = el.getBoundingClientRect().top
    screenTopOnStart = screenTop
  }

  function recoverScreenTopPositionOnStart(e: Sortable.SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(WALL_CONTAINER_ID)
    const screenTop = el.getBoundingClientRect().top
    const offset = screenTopOnStart - screenTop
    const newScrollTop = (scrollingElement?.scrollTop ?? 0) - offset
    if (newScrollTop < 0) {
      addTemporaryListTopPadding(-newScrollTop)
    }
    if (newScrollTop + window.innerHeight > (scrollingElement?.scrollHeight ?? Infinity)) {
      addTemporaryListBottomPadding(scrollingElement?.scrollHeight ?? 0 - newScrollTop - window.innerHeight)
    }
    if (scrollingElement != null) scrollingElement.scrollTop = newScrollTop
  }

  function rememberScreenTopPositionOnEnd(e: Sortable.SortableEvent): void {
    screenTopOnEnd = e.item.getBoundingClientRect().top
  }

  function recoverScreenTopPositionOnEnd(e: Sortable.SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(WALL_CONTAINER_ID)
    const screenTop = el.getBoundingClientRect().top
    const offset = screenTopOnEnd - screenTop
    const newScrollTop = (scrollingElement?.scrollTop ?? 0) - offset
    if (scrollingElement != null) scrollingElement.scrollTop = newScrollTop
  }

  function initializeSortable(): void {
    const wishListElement = document.getElementById(WISH_LIST_ID) as HTMLDivElement
    // https://github.com/SortableJS/Sortable#options
    void import('sortablejs').then(({ default: Sortable }) => {
      sortable = new Sortable(wishListElement, {
        ...getSortableOptions('section', format.value),
        disabled: !canIDragSections.value,
        direction: format.value === 'grid' || format.value === 'columns' ? 'horizontal' : 'vertical',
        filter: NO_DRAG_SELECTOR,
        preventOnFilter: false,
        ghostClass: 'sortable-indicator-line',
        dragClass: 'sortable-dragging-clone',
        draggable: SECTION_SELECTOR,
        handle: GROUP_HEADER_SELECTOR,
        onClone: () => {
          setDirectionForDraggingElement(dir.value)
        },
        onStart: (e) => {
          onDragStart?.(toSectionDragEvent(e))
          if (format.value === 'timeline' || format.value === 'timeline_v2') {
            rememberScreenTopPositionOnStart(e)
            unwrapTimelineLayout()
            createPersistentClone(e)
            forceChangeCursor('grabbing')
            setDraggingStyles()
            recoverScreenTopPositionOnStart(e)
          }
          if (format.value === 'grid' || format.value === 'columns') {
            createPersistentClone(e)
            forceChangeCursor('grabbing')
            setGridDraggingStyles()
          }
          if (format.value === 'matrix' || format.value === 'stream') {
            createPersistentClone(e)
            rememberScreenTopPositionOnStart(e)
            forceChangeCursor('grabbing')
            setDraggingStyles()
            recoverScreenTopPositionOnStart(e)
          }
        },
        onMove: (e) => {},
        onEnd: (e) => {
          removePersistentCloneWrapperElement()
          rememberScreenTopPositionOnEnd(e)
          removeDraggingStyles()
          resetCursor()
          removeTemporaryListPadding()
          recoverScreenTopPositionOnEnd(e)
          onDragEnd?.(toSectionDragEvent(e))
        },
      })
    })
  }

  function addDefaultStyles(): void {
    if (format.value === 'grid' || format.value === 'columns') {
      sortableDefaultsStyleTag = appendCssToDocumentHead(
        `
        ${SECTION_SELECTOR}.sortable-persistent-clone {
          opacity: 0.5;
        }
        ${SECTION_SELECTOR}.sortable-dragging-clone {
          opacity: 1 !important;
          transform: rotate(${isRtl.value ? '2deg' : '-2deg'});
          box-shadow: none !important;
          filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.32));
        }
        ${SECTION_SELECTOR}.sortable-dragging-clone header > button {
          visibility: hidden;
        }
        ${SECTION_SELECTOR}.sortable-dragging-clone .group-posts {
          visibility: hidden;
        }
      `,
      )
      return
    }
    sortableDefaultsStyleTag = appendCssToDocumentHead(
      `
        ${SECTION_SELECTOR}.sortable-persistent-clone {
          opacity: 0.5;
        }
        ${SECTION_SELECTOR}.sortable-dragging-clone {
          opacity: 1 !important;
          transform: rotate(${isRtl.value ? '2deg' : '-2deg'});
          box-shadow: none !important;
          filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.32));
        }
      `,
    )
  }

  function removeDefaultStyles(): void {
    sortableDefaultsStyleTag?.remove()
    sortableDefaultsStyleTag = null
  }

  function destroySortable(): void {
    sortable?.destroy()
  }

  watch([isOverlayVisible, canIDragSections], ([isVisible, canDrag]) => {
    if (isVisible || !canDrag) {
      sortable?.option('disabled', true)
    } else {
      sortable?.option('disabled', false)
    }
  })

  const initializeDragAndDrop = (): void => {
    nextTick(() => {
      addDefaultStyles()
      initializeSortable()
      stopShouldDisableDraggingPostWatcher()
    })
  }

  const stopShouldDisableDraggingPostWatcher = watch(shouldDisableDraggingPost, (newShouldDisableDraggingPost) => {
    if (!newShouldDisableDraggingPost) {
      initializeDragAndDrop()
    }
  })

  onMounted(() => {
    if (!shouldDisableDraggingPost.value) {
      initializeDragAndDrop()
    }
  })

  onUnmounted(() => {
    removeDefaultStyles()
    destroySortable()
  })
}
